import CrudPage from "./CrudPage";

export const AdminBanner = () => (
  <CrudPage
    testKey="banner"
    title="Announcements Banner"
    description="Items that scroll across the top of every page."
    endpoint="/banner"
    sortable
    reorderCollection="banner_items"
    defaultValues={{ text: "", kind: "alert", active: true, order: 0 }}
    fields={[
      { name: "text", label: "Banner text", hint: "Plain text — keep it short, this scrolls across the top.", multiline: true, rows: 3, placeholder: "e.g. Next meeting: June 9, 7:00 PM" },
      { name: "kind", label: "Type", hint: "Choose 'meeting' to show a calendar icon, or 'alert' for a warning icon.", options: ["alert", "meeting"] },
      { name: "active", label: "Active", type: "boolean", placeholder: "Show this on the public site" },
    ]}
    listColumns={[
      { key: "text", label: "Text" },
      { key: "kind", label: "Type" },
      { key: "active", label: "Active" },
    ]}
  />
);

export const AdminMinutes = () => (
  <CrudPage
    testKey="minutes"
    title="Meeting Minutes"
    description="Records of township board meetings."
    endpoint="/minutes"
    defaultValues={{ title: "", meeting_date: "", summary: "", content: "", pdf_url: "", pdf_file_id: "" }}
    fields={[
      { name: "title", label: "Title", placeholder: "Town Board Meeting — June 9, 2026" },
      { name: "meeting_date", label: "Meeting date", type: "date" },
      { name: "summary", label: "Summary", hint: "A one-sentence overview that appears in the list.", multiline: true, rows: 2 },
      { name: "content", label: "Full minutes (text)", hint: "Paste or type the full text of the minutes here.", multiline: true, rows: 10 },
      { name: "pdf_file_id", label: "Upload document (optional)", hint: "Drag-and-drop a PDF or Word document (.pdf, .doc, .docx) — or click to browse.", type: "file_upload", fileKind: "document" },
      { name: "pdf_url", label: "External document URL (used only if no upload above)" },
    ]}
    listColumns={[
      { key: "title", label: "Title" },
      { key: "meeting_date", label: "Date" },
    ]}
  />
);

export const AdminPostings = () => (
  <CrudPage
    testKey="postings"
    title="Public Notices"
    description="Township notices, announcements, and bid/RFP postings."
    endpoint="/postings"
    defaultValues={{ title: "", body: "", category: "notice", posting_date: "", signed_by: "", pinned: false, file_id: "" }}
    fields={[
      { name: "title", label: "Title", placeholder: "Notice to Lake Eunice Township Residents — Road Tour 2025" },
      { name: "category", label: "Category", options: ["notice", "announcement", "bid", "other"] },
      { name: "posting_date", label: "Posting date", type: "date" },
      { name: "pinned", label: "Pinned (highlight at top)", type: "boolean" },
      { name: "body", label: "Body", hint: "The full text of the notice. Line breaks are preserved.", multiline: true, rows: 8 },
      { name: "signed_by", label: "Signed by", placeholder: "Heather Anderson, Lake Eunice Township Clerk" },
      { name: "file_id", label: "Attached document (optional)", hint: "Upload the original PDF or Word document (.pdf, .doc, .docx).", type: "file_upload", fileKind: "document" },
    ]}
    listColumns={[
      { key: "title", label: "Title" },
      { key: "category", label: "Category" },
      { key: "posting_date", label: "Date" },
    ]}
  />
);

export const AdminEvents = () => (
  <CrudPage
    testKey="events"
    title="Events Calendar"
    description="Meetings and events visible on the public calendar."
    endpoint="/events"
    defaultValues={{ title: "", event_date: "", time: "", location: "", description: "" }}
    fields={[
      { name: "title", label: "Title", placeholder: "Monthly Town Board Meeting" },
      { name: "event_date", label: "Date", type: "date" },
      { name: "time", label: "Time", placeholder: "7:00 PM" },
      { name: "location", label: "Location", placeholder: "Lake Eunice Town Hall" },
      { name: "description", label: "Description", multiline: true, rows: 3 },
    ]}
    listColumns={[
      { key: "title", label: "Title" },
      { key: "event_date", label: "Date" },
      { key: "time", label: "Time" },
    ]}
  />
);

export const AdminOfficials = () => (
  <CrudPage
    testKey="officials"
    title="Officials"
    description="Township board members and their contact info."
    endpoint="/officials"
    sortable
    defaultValues={{ name: "", title: "", phone: "", email: "", order: 0 }}
    fields={[
      { name: "name", label: "Name" },
      { name: "title", label: "Title (Chairman, Supervisor, etc.)" },
      { name: "phone", label: "Phone" },
      { name: "email", label: "Email" },
    ]}
    listColumns={[
      { key: "name", label: "Name" },
      { key: "title", label: "Title" },
      { key: "phone", label: "Phone" },
    ]}
  />
);

export const AdminProjects = () => (
  <CrudPage
    testKey="projects"
    title="Projects"
    description="Ongoing and completed initiatives."
    endpoint="/projects"
    defaultValues={{ title: "", status: "ongoing", description: "", year: "" }}
    fields={[
      { name: "title", label: "Title" },
      { name: "status", label: "Status", options: ["ongoing", "completed", "planned"] },
      { name: "year", label: "Year" },
      { name: "description", label: "Description", multiline: true, rows: 4 },
    ]}
    listColumns={[
      { key: "title", label: "Title" },
      { key: "status", label: "Status" },
      { key: "year", label: "Year" },
    ]}
  />
);

export const AdminNotices = () => (
  <CrudPage
    testKey="notices"
    title="Information Notices"
    description="Notices that appear on the Information page."
    endpoint="/notices"
    defaultValues={{ title: "", body: "", category: "general", pinned: false, image_file_ids: [] }}
    fields={[
      { name: "title", label: "Title" },
      { name: "category", label: "Category", options: ["general", "safety", "policy", "services"] },
      { name: "pinned", label: "Pinned (highlight at top)", type: "boolean" },
      { name: "body", label: "Body", hint: "Plain text — line breaks are preserved.", multiline: true, rows: 8 },
      { name: "image_file_ids", label: "Images (optional)", hint: "Drag-and-drop images, or click. They appear as a small gallery on the public notice.", type: "multi_image" },
    ]}
    listColumns={[
      { key: "title", label: "Title" },
      { key: "category", label: "Category" },
      { key: "pinned", label: "Pinned" },
    ]}
  />
);

export const AdminLinks = () => (
  <CrudPage
    testKey="links"
    title="Helpful Links"
    description="External links on the public Links page."
    endpoint="/links"
    sortable
    defaultValues={{ label: "", url: "", category: "general", order: 0 }}
    fields={[
      { name: "label", label: "Label" },
      { name: "url", label: "URL" },
      { name: "category", label: "Category" },
    ]}
    listColumns={[
      { key: "label", label: "Label" },
      { key: "url", label: "URL" },
      { key: "category", label: "Category" },
    ]}
  />
);

export const AdminGallery = () => (
  <CrudPage
    testKey="gallery"
    title="Photo Gallery"
    description="Images shown on the public Information page."
    endpoint="/gallery"
    sortable
    defaultValues={{ title: "", caption: "", location: "", order: 0, file_id: "" }}
    fields={[
      { name: "title", label: "Title", placeholder: "Sunset over Cormorant Lake" },
      { name: "location", label: "Location", placeholder: "Cormorant Lake" },
      { name: "caption", label: "Caption", multiline: true, rows: 2 },
      { name: "file_id", label: "Image", hint: "Drag-and-drop or click to upload.", type: "file_upload", fileKind: "image" },
    ]}
    listColumns={[
      { key: "title", label: "Title" },
      { key: "location", label: "Location" },
    ]}
  />
);
